import { useState, useEffect } from "react";
import { Button } from "../../components/button.component";
import { ApiFinance } from "../../core/apis";
import { DatePicker, SelectPicker } from "rsuite";
import {
  EnumInvoiceGenerationInvokeType,
  generateDropdownListForEnumInvoiceGenerationInvokeType,
} from "../../libs/enums/EnumPatientInvoketype";
import { UtilityDate } from "../../utils";

// Define type for SelectPicker value
type ValueType = string | number | null;

export function GenerateInvoice() {
  const {  previousMonth, previousMonthYear } =
    UtilityDate.getPreviousInfo();
  const [isPending, setIsPending] = useState(false);
  const [invokeType, setInvokeType] = useState(
    EnumInvoiceGenerationInvokeType.Monthly.toString()
  );
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedMonth, setSelectedMonth] = useState(previousMonth);
  const [selectedYear, setSelectedYear] = useState(previousMonthYear);
  const [selectedWeek, setSelectedWeek] = useState(0);

  const invoiceGenerationInvokeTypes =
    generateDropdownListForEnumInvoiceGenerationInvokeType();

  const monthOptions = [
    { label: "January", value: 0 },
    { label: "February", value: 1 },
    { label: "March", value: 2 },
    { label: "April", value: 3 },
    { label: "May", value: 4 },
    { label: "June", value: 5 },
    { label: "July", value: 6 },
    { label: "August", value: 7 },
    { label: "September", value: 8 },
    { label: "October", value: 9 },
    { label: "November", value: 10 },
    { label: "December", value: 11 },
  ];

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => ({
    label: String(currentYear - i),
    value: currentYear - i,
  }));

  useEffect(() => {
    // We're now using DatePicker for all types except Monthly
    // So we'll only set the date for Monthly invoke type
    if (invokeType === EnumInvoiceGenerationInvokeType.Monthly.toString()) {
      const newDate = new Date(selectedYear, selectedMonth, 16);
      const currentDate = new Date();
      
      if (newDate > currentDate) {
        setSelectedDate(currentDate);
      } else {
        setSelectedDate(newDate);
      }
    }
  }, [invokeType, selectedMonth, selectedYear]);

  const handleInvokeTypeChange = (value: ValueType) => {
    if (value && typeof value === "string") {
      setInvokeType(value);
      setSelectedWeek(0);
      // Reset the selected date when changing invoke type
      setSelectedDate(undefined);
    }
  };

  const handleGenerate = async () => {
    setIsPending(true);
    await ApiFinance.GenerateInvoiceV5(invokeType, selectedDate!);
    setIsPending(false);
  };

  const renderDateSelection = () => {
    const currentDate = new Date();
    const isDisabledMonth = (month: number) => {
      if (selectedYear === currentDate.getFullYear()) {
        return month >= currentDate.getMonth();
      }
      return false;
    };

    const isDisabledYear = (year: number) => year > currentDate.getFullYear();

    switch (invokeType) {
      case EnumInvoiceGenerationInvokeType.Monthly.toString():
        return (
          <>
            <SelectPicker
              data={monthOptions}
              searchable={false}
              placeholder="Select Month"
              value={selectedMonth}
              onChange={(value: ValueType) => {
                if (value !== null && typeof value === "number") {
                  setSelectedMonth(value);
                }
              }}
              disabledItemValues={monthOptions
                .filter((month) => isDisabledMonth(month.value))
                .map((month) => month.value)}
              className="col-md-2 col-2"
            />
            <SelectPicker
              data={yearOptions}
              searchable={false}
              placeholder="Select Year"
              value={selectedYear}
              onChange={(value: ValueType) => {
                if (value !== null && typeof value === "number") {
                  setSelectedYear(value);
                  setSelectedMonth(0);
                  setSelectedWeek(0);
                }
              }}
              disabledItemValues={yearOptions
                .filter((year) => isDisabledYear(year.value))
                .map((year) => year.value)}
              className="col-md-2 col-2"
            />
          </>
        );
      case EnumInvoiceGenerationInvokeType.Weekly.toString():
      case EnumInvoiceGenerationInvokeType.Biweekly.toString():
      case EnumInvoiceGenerationInvokeType.Daily.toString():
      default:
        return (
          <DatePicker
            key={selectedDate?.toString()}
            value={selectedDate}
            onClean={() => setSelectedDate(undefined)}
            onChange={(d) => setSelectedDate(d)}
            placeholder="Select Date"
            oneTap
            className="col-md-2 col-2"
            disabledDate={(date: Date | undefined) =>
              date ? date >= new Date(new Date().setHours(0, 0, 0, 0)) : false
            }
          />
        );
    }
  };

  return (
    <div className="row offset-md-3 offset-3">
      <SelectPicker
        data={invoiceGenerationInvokeTypes}
        searchable={false}
        placeholder="Select Invoke Type"
        value={invokeType}
        onChange={handleInvokeTypeChange}
        className="col-md-2 col-2"
      />

      {renderDateSelection()}

      <Button
        name="Generate Invoice"
        type="button"
        className="btn-green col-md-2 col-2"
        onClick={handleGenerate}
        disabled={isPending || !invokeType || !selectedDate}
        loading={isPending}
      />
    </div>
  );
}
